import {
  ComponentRef,
  inject,
  Injectable,
  Signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ModalService } from '@ndwnu/design-system';
import { CustomModalComponent } from '@daisi/components/custom-modal/custom-modal.component';
import { LoggingService } from '@daisi/services/logging/logging.service';

@Injectable({
  providedIn: 'root',
})
export class CustomModalService {
  container: ViewContainerRef | undefined;
  private readonly modalService: ModalService = inject(ModalService);
  private readonly log: LoggingService = inject(LoggingService);

  setViewContainerRef(container: ViewContainerRef): void {
    this.container = container;
  }

  public create<T extends ContainsModal>(componentType: new (...args: any[]) => T): T | undefined {
    if (!this.container) {
      this.log.error('No view container ref set, cannot create modal.');
      return;
    }
    const componentRef: ComponentRef<T> = this.container.createComponent(componentType);
    const component: T = componentRef.instance;

    component.modalRef().closed.subscribe((): void => {
      componentRef.destroy();
      this.close();
    });
    return component;
  }

  public createAndOpen<T extends ContainsModal>(
    componentType: new (...args: any[]) => T,
  ): T | undefined {
    if (!this.container) {
      this.log.error('No view container ref set, cannot create modal.');
      return;
    }
    const componentRef: ComponentRef<T> = this.container.createComponent(componentType);
    const component: T = componentRef.instance;

    component.modalRef().closed.subscribe((): void => {
      componentRef.destroy();
      this.close();
    });

    component.modalRef().open();
    return component;
  }

  open(templateRef: TemplateRef<unknown>): void {
    this.modalService.open(templateRef, this.container);
  }

  close(): void {
    this.modalService.close();
  }
}

export interface ContainsModal {
  modalRef: Signal<CustomModalComponent>;
}
